import { ApolloClient, InMemoryCache, HttpLink, ApolloLink } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { CombinedGraphQLErrors, CombinedProtocolErrors } from "@apollo/client/errors";
import { SetContextLink } from "@apollo/client/link/context";
import { graphQlInterceptorLink } from "./graphQl.interceptorLink";
import { graphQlVariablesLink } from "./graphQlVariablesLink";
import { createLogger, type ILogger } from "@/infrastructure/services/logger";
import { AppConfig } from "@src/config/AppConfig";
import { AuthUserTokenService } from "@src/infrastructure/auth/AuthUserTokenService";

const GRAPHQL_BASE_URL = AppConfig.getGraphQLUrl();

export class GraphQlAdapter {
  private readonly client: ApolloClient;
  private static instance: GraphQlAdapter;
  private readonly logger: ILogger = createLogger("GraphQlAdapter");

  private constructor() {
    this.client = this.initializeClient();
  }

  public static getInstance(): GraphQlAdapter {
    if (!GraphQlAdapter.instance) {
      GraphQlAdapter.instance = new GraphQlAdapter();
    }
    return GraphQlAdapter.instance;
  }

  protected async getUserAccessToken() {
    const userAccessToken = await new AuthUserTokenService().getAuthAccessToken();
    return userAccessToken;
  }

  protected async getAdminAccessToken() {
    const adminAccessToken = await new AuthUserTokenService().getAdminAccessToken();
    return adminAccessToken;
  }

  protected async getAdminRefreshToken() {
    const adminRefreshToken = await new AuthUserTokenService().getAdminRefreshToken();
    return adminRefreshToken;
  }

  private async getAuthTokens() {
    const userAccessToken = await this.getUserAccessToken();
    const adminAccessToken = await this.getAdminAccessToken();
    const adminRefreshToken = await this.getAdminRefreshToken();
    return { userAccessToken, adminAccessToken, adminRefreshToken };
  }

  

  private initializeClient(): ApolloClient {
    let userAccessToken: string | null = null;
    let adminAccessToken: string | null = null;
    let adminRefreshToken: string | null = null;
    const httpLink = new HttpLink({
      uri: GRAPHQL_BASE_URL,
    });

    const errorLink = new ErrorLink(({ error }) => {
      if (CombinedGraphQLErrors.is(error)) {
        for (const { message, locations, path } of error.errors) {
          this.logger.error(`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`, error);
        }
      } else if (CombinedProtocolErrors.is(error)) {
        for (const { message, extensions } of error.errors) {
          this.logger.error(`[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`, error);
        }
      } else {
        this.logger.error(`[Network error]: ${error.message}`, error  );
      }
    });

    const authLink = new SetContextLink(async prevContext => {
      const tokens = await this.getAuthTokens();
      userAccessToken = tokens.userAccessToken;
      adminAccessToken = tokens.adminAccessToken;
      adminRefreshToken = tokens.adminRefreshToken;

      return {
        ...prevContext,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: userAccessToken ? `Bearer ${userAccessToken}` : undefined,
          "X-Admin-Authorization": adminAccessToken ? `Bearer ${adminAccessToken}` : undefined,
          "X-Admin-Refresh-Token": adminRefreshToken ? `Bearer ${adminRefreshToken}` : undefined,
        },
      };
    });

    return new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, graphQlInterceptorLink, graphQlVariablesLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          errorPolicy: "all",
        },
        query: {
          errorPolicy: "all",
        },
        mutate: {
          errorPolicy: "all",
        },
      },
    });
  }

  getClient(): ApolloClient {
    return this.client;
  }
}
